import { FieldInstructionBundle } from 'containers/FormBuilder'
import * as fields from 'form/fields'
import {
    createStateFieldWithEligibilityValidation,
    createZipFieldInstruction,
} from 'form/fields/addressFields'
import DisclosureType from 'state/enums/DisclosureType'

import { gridColumnWrapper } from '../gridColumnWrapper'
import sharedInstructions, {
    applicantFieldNames,
    coapplicantFieldNames,
    otherFieldNames,
} from '../sharedInstructions'
import { AutomobileSubmissionFormInstructions } from './AutomobileFormBuilder'

export const automobileSpecificFieldNames = {
    stateEligibility: 'stateEligibility',
}

export const collateralFieldNames = {
    vehicle: 'vehicle',
}

export const consentFieldNames = {
    consent: 'consent',
}

export const tradeInFieldNames = {
    intent: 'intent',
}

const collateralFields: FieldInstructionBundle = {
    // key must be `vehicle` because of how vehicle selection components work
    [collateralFieldNames.vehicle]: fields.vehicleWriteInAutomobile,
}

const stateField = createStateFieldWithEligibilityValidation(
    applicantFieldNames.state,
    automobileSpecificFieldNames.stateEligibility,
)

const zipCodeField = createZipFieldInstruction({
    state: applicantFieldNames.state,
    city: applicantFieldNames.city,
    stateEligibility: automobileSpecificFieldNames.stateEligibility,
})

// re-arranges the applicant fields from various sharedInstructions bundles
const applicantInformationFields: FieldInstructionBundle = {
    [applicantFieldNames.firstName]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[
            applicantFieldNames.firstName
        ],
        'span 6',
    ),
    [applicantFieldNames.middleInitial]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[
            applicantFieldNames.middleInitial
        ],
        'span 3',
    ),
    [applicantFieldNames.lastName]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[
            applicantFieldNames.lastName
        ],
        'span 6',
    ),
    [applicantFieldNames.suffix]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[applicantFieldNames.suffix],
        'span 3',
    ),
    [applicantFieldNames.ssn]: gridColumnWrapper(
        sharedInstructions.applicantIdentityFields[applicantFieldNames.ssn],
        'span 6',
    ),
    [applicantFieldNames.street]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[applicantFieldNames.street],
        'span 16',
    ),
    [applicantFieldNames.apartmentSuite]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[
            applicantFieldNames.apartmentSuite
        ],
        'span 8',
    ),
    [applicantFieldNames.zip]: gridColumnWrapper(zipCodeField, 'span 8'),
    [applicantFieldNames.city]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[applicantFieldNames.city],
        'span 8',
    ),
    [applicantFieldNames.state]: gridColumnWrapper(stateField, 'span 8'),
    [applicantFieldNames.email]: gridColumnWrapper(
        sharedInstructions.contactInfoFields[applicantFieldNames.email],
        'span 16',
    ),
    [applicantFieldNames.dateOfBirth]: gridColumnWrapper(
        sharedInstructions.applicantPersonalFields[
            applicantFieldNames.dateOfBirth
        ],
        'span 8',
    ),
    [applicantFieldNames.residenceStatus]: gridColumnWrapper(
        sharedInstructions.residenceFields[applicantFieldNames.residenceStatus],
        'span 8',
    ),
    [applicantFieldNames.residentialYears]: gridColumnWrapper(
        sharedInstructions.residenceFields[
            applicantFieldNames.residentialYears
        ],
        'span 4',
    ),
    [applicantFieldNames.residentialMonths]: gridColumnWrapper(
        sharedInstructions.residenceFields[
            applicantFieldNames.residentialMonths
        ],
        'span 4',
    ),
    [applicantFieldNames.monthlyHousingPayment]: gridColumnWrapper(
        sharedInstructions.residenceFields[
            applicantFieldNames.monthlyHousingPayment
        ],
        'span 8',
    ),
    [applicantFieldNames.homePhone]: gridColumnWrapper(
        sharedInstructions.contactInfoFields[applicantFieldNames.homePhone],
        'span 8',
    ),
    [applicantFieldNames.mobilePhone]: gridColumnWrapper(
        sharedInstructions.contactInfoFields[applicantFieldNames.mobilePhone],
        'span 8',
    ),
}

// re-orders the employment fields from sharedInstructions
const employmentFields: FieldInstructionBundle = {
    [applicantFieldNames.employmentStatus]:
        sharedInstructions.employmentFields[
            applicantFieldNames.employmentStatus
        ],
    [applicantFieldNames.employerGrossAmount]:
        sharedInstructions.employmentFields[
            applicantFieldNames.employerGrossAmount
        ],
    [applicantFieldNames.employerGrossInterval]:
        sharedInstructions.employmentFields[
            applicantFieldNames.employerGrossInterval
        ],
    [applicantFieldNames.jobTitle]:
        sharedInstructions.employmentFields[applicantFieldNames.jobTitle],
    [applicantFieldNames.employerName]:
        sharedInstructions.employmentFields[applicantFieldNames.employerName],
    [applicantFieldNames.employerYears]:
        sharedInstructions.employmentFields[applicantFieldNames.employerYears],
    [applicantFieldNames.employerMonths]:
        sharedInstructions.employmentFields[applicantFieldNames.employerMonths],
    [applicantFieldNames.workPhone]:
        sharedInstructions.employmentFields[applicantFieldNames.workPhone],
    [applicantFieldNames.hasOtherIncome]:
        sharedInstructions.employmentFields[applicantFieldNames.hasOtherIncome],
    [applicantFieldNames.otherIncomeSource]:
        sharedInstructions.employmentFields[
            applicantFieldNames.otherIncomeSource
        ],
    [applicantFieldNames.otherIncomeGrossAmount]:
        sharedInstructions.employmentFields[
            applicantFieldNames.otherIncomeGrossAmount
        ],
    [applicantFieldNames.otherIncomeGrossInterval]:
        sharedInstructions.employmentFields[
            applicantFieldNames.otherIncomeGrossInterval
        ],
    [applicantFieldNames.removeOtherIncome]:
        sharedInstructions.employmentFields[
            applicantFieldNames.removeOtherIncome
        ],
}

// re-arranges the co-applicant fields from various sharedInstructions bundles
const coapplicantInformationFields1: FieldInstructionBundle = {
    [coapplicantFieldNames.firstName]: gridColumnWrapper(
        sharedInstructions.coapplicantPersonalFields[
            coapplicantFieldNames.firstName
        ],
        'span 6',
    ),
    [coapplicantFieldNames.middleInitial]: gridColumnWrapper(
        sharedInstructions.coapplicantPersonalFields[
            coapplicantFieldNames.middleInitial
        ],
        'span 3',
    ),
    [coapplicantFieldNames.lastName]: gridColumnWrapper(
        sharedInstructions.coapplicantPersonalFields[
            coapplicantFieldNames.lastName
        ],
        'span 6',
    ),
    [coapplicantFieldNames.suffix]: gridColumnWrapper(
        sharedInstructions.coapplicantPersonalFields[
            coapplicantFieldNames.suffix
        ],
        'span 3',
    ),
    [coapplicantFieldNames.ssn]: gridColumnWrapper(
        sharedInstructions.coapplicantIdentityFields[coapplicantFieldNames.ssn],
        'span 6',
    ),
    [otherFieldNames.isCoapplicantAddressSame]: gridColumnWrapper(
        sharedInstructions.coapplicantPersonalFields[
            otherFieldNames.isCoapplicantAddressSame
        ],
        '1 / span 24',
    ),
}

const coapplicantAddressFields: FieldInstructionBundle = {
    [coapplicantFieldNames.street]: gridColumnWrapper(
        sharedInstructions.coapplicantAddressFields[
            coapplicantFieldNames.street
        ],
        'span 16',
    ),
    [coapplicantFieldNames.apartmentSuite]: gridColumnWrapper(
        sharedInstructions.coapplicantAddressFields[
            coapplicantFieldNames.apartmentSuite
        ],
        'span 8',
    ),
    [coapplicantFieldNames.zip]: gridColumnWrapper(
        sharedInstructions.coapplicantAddressFields[coapplicantFieldNames.zip],
        'span 8',
    ),
    [coapplicantFieldNames.city]: gridColumnWrapper(
        sharedInstructions.coapplicantAddressFields[coapplicantFieldNames.city],
        'span 8',
    ),
    [coapplicantFieldNames.state]: gridColumnWrapper(
        sharedInstructions.coapplicantAddressFields[
            coapplicantFieldNames.state
        ],
        'span 8',
    ),
}

const coapplicantInformationFields2: FieldInstructionBundle = {
    [coapplicantFieldNames.email]: gridColumnWrapper(
        sharedInstructions.coapplicantContactInfoFields[
            coapplicantFieldNames.email
        ],
        'span 16',
    ),
    [coapplicantFieldNames.dateOfBirth]: gridColumnWrapper(
        sharedInstructions.coapplicantPersonalFields[
            coapplicantFieldNames.dateOfBirth
        ],
        'span 8',
    ),
    [coapplicantFieldNames.residenceStatus]: gridColumnWrapper(
        sharedInstructions.coapplicantResidenceFields[
            coapplicantFieldNames.residenceStatus
        ],
        'span 8',
    ),
    [coapplicantFieldNames.residentialYears]: gridColumnWrapper(
        sharedInstructions.coapplicantResidenceFields[
            coapplicantFieldNames.residentialYears
        ],
        'span 4',
    ),
    [coapplicantFieldNames.residentialMonths]: gridColumnWrapper(
        sharedInstructions.coapplicantResidenceFields[
            coapplicantFieldNames.residentialMonths
        ],
        'span 4',
    ),
    [coapplicantFieldNames.monthlyHousingPayment]: gridColumnWrapper(
        sharedInstructions.coapplicantResidenceFields[
            coapplicantFieldNames.monthlyHousingPayment
        ],
        'span 8',
    ),
    [coapplicantFieldNames.homePhone]: gridColumnWrapper(
        sharedInstructions.coapplicantContactInfoFields[
            coapplicantFieldNames.homePhone
        ],
        'span 8',
    ),
    [coapplicantFieldNames.mobilePhone]: gridColumnWrapper(
        sharedInstructions.coapplicantContactInfoFields[
            coapplicantFieldNames.mobilePhone
        ],
        'span 8',
    ),
}

// re-orders the co-app employment fields from sharedInstructions
const coapplicantEmploymentFields: FieldInstructionBundle = {
    [coapplicantFieldNames.employmentStatus]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.employmentStatus
        ],
    [coapplicantFieldNames.employerGrossAmount]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.employerGrossAmount
        ],
    [coapplicantFieldNames.employerGrossInterval]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.employerGrossInterval
        ],
    [coapplicantFieldNames.jobTitle]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.jobTitle
        ],
    [coapplicantFieldNames.employerName]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.employerName
        ],
    [coapplicantFieldNames.employerYears]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.employerYears
        ],
    [coapplicantFieldNames.employerMonths]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.employerMonths
        ],
    [coapplicantFieldNames.workPhone]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.workPhone
        ],
    [coapplicantFieldNames.hasOtherIncome]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.hasOtherIncome
        ],
    [coapplicantFieldNames.otherIncomeSource]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.otherIncomeSource
        ],
    [coapplicantFieldNames.otherIncomeGrossAmount]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.otherIncomeGrossAmount
        ],
    [coapplicantFieldNames.otherIncomeGrossInterval]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.otherIncomeGrossInterval
        ],
    [coapplicantFieldNames.removeOtherIncome]:
        sharedInstructions.coapplicantEmploymentFields[
            coapplicantFieldNames.removeOtherIncome
        ],
}

const consentFields: FieldInstructionBundle = {
    [consentFieldNames.consent]: fields.createConsentCheckbox(
        DisclosureType.AUTOMOBILE_APPLICATION_FORM_DISCLOSURE,
    ),
}

const tradeInFields: FieldInstructionBundle = {
    [tradeInFieldNames.intent]: fields.tradeInFields,
}

export const automobileFormInstructions: AutomobileSubmissionFormInstructions =
    {
        financeManagerFields: sharedInstructions.financeManagerFields,
        applicationTypeFields: sharedInstructions.applicationTypeFields,
        relationOfCoapplicantFields:
            sharedInstructions.relationOfCoapplicantFields,
        verificationFields: sharedInstructions.verificationFields,
        tradeInFields,
        applicantInformationFields,
        employmentFields,
        coapplicantInformationFields1,
        coapplicantAddressFields,
        coapplicantInformationFields2,
        coapplicantEmploymentFields,
        collateralFields,
        consentFields,
    }
